/**
 * Helpers puros para a árvore de grupos (`/managed-device-groups/tree`).
 *
 * A árvore é única por tenant — a mesma estrutura aparece em todas as abas de
 * tipo. O que muda por aba é a CONTAGEM: cada aba conta só os devices do seu
 * próprio tipo dentro do grupo (e de toda a subárvore).
 */

/** Filhos diretos de `parentId` (null = raiz). */
export function getChildGroups(tree, parentId) {
  if (!parentId) return tree || [];
  const node = findGroupNode(tree, parentId);
  return node?.children || [];
}

/** Nó do grupo em qualquer profundidade. */
export function findGroupNode(tree, groupId) {
  if (!groupId) return null;
  for (const node of tree || []) {
    if (node.id === groupId) return node;
    const found = findGroupNode(node.children, groupId);
    if (found) return found;
  }
  return null;
}

/** Caminho raiz→grupo como [{ id, name }]; [] quando o grupo não existe mais. */
export function findGroupPath(tree, groupId) {
  if (!groupId) return [];
  for (const node of tree || []) {
    if (node.id === groupId) return [{ id: node.id, name: node.name }];
    const sub = findGroupPath(node.children, groupId);
    if (sub.length > 0) return [{ id: node.id, name: node.name }, ...sub];
  }
  return [];
}

/** Ids do grupo + toda a subárvore. */
export function collectSubtreeIds(node) {
  if (!node) return [];
  const ids = [node.id];
  for (const child of node.children || []) ids.push(...collectSubtreeIds(child));
  return ids;
}

/** Quantos devices da lista caem no grupo ou em qualquer descendente. */
export function countDevicesInSubtree(node, devices) {
  const ids = new Set(collectSubtreeIds(node));
  return (devices || []).filter((d) => d.managedDeviceGroupId && ids.has(d.managedDeviceGroupId)).length;
}

/** Devices diretamente no grupo (`null` = raiz, sem grupo). */
export function devicesInGroup(devices, groupId) {
  return (devices || []).filter((d) => (d.managedDeviceGroupId ?? null) === (groupId ?? null));
}

/**
 * Impede mover um grupo para dentro de si mesmo (loop na árvore).
 * `targetId` null = raiz, sempre permitido.
 */
export function isDescendantOf(tree, targetId, groupId) {
  if (!targetId) return false;
  if (targetId === groupId) return true;
  const node = findGroupNode(tree, groupId);
  return collectSubtreeIds(node).includes(targetId);
}
